// Matched collections lead their search section newest first, and the first
// few take the paginated slots, so the order decides what a visitor sees on
// page one. A collection is dated by its own release_date, or failing that
// by the start of its first drop: Triple Trouble has 83 matched artworks and
// dropped in October 2025 but no release_date, and the documents' plain
// `-release_date` sort — NULLs first in Postgres, and Directus cannot ask
// otherwise — put it ahead of Depictions, released that morning. This
// re-sorts a fetched page the way the REST endpoint orders its rows: by that
// effective date, newest first, undated last, ties by name. The sort is
// stable, so what it does not distinguish keeps the query's order.
type Dated = {
	release_date?: string | null;
	name?: string | null;
	drop_page?: {
		drop_config?: ({ drop_start_date?: string | null } | null)[] | null;
	} | null;
};

const time = (value: string | null | undefined) =>
	value ? new Date(value).getTime() : Number.NaN;

// The collection's own release date, else the earliest of its drops' start
// dates, else NaN.
const releaseTime = (row: Dated) => {
	const own = time(row.release_date);

	if (!Number.isNaN(own)) return own;

	const starts = (row.drop_page?.drop_config || [])
		.map((config) => time(config?.drop_start_date))
		.filter((start) => !Number.isNaN(start));

	return starts.length ? Math.min(...starts) : Number.NaN;
};

export const newestFirst = <TRow extends Dated | null>(rows: TRow[]): TRow[] =>
	[...rows].sort((left, right) => {
		const leftTime = left ? releaseTime(left) : Number.NaN;
		const rightTime = right ? releaseTime(right) : Number.NaN;
		const leftDated = !Number.isNaN(leftTime);
		const rightDated = !Number.isNaN(rightTime);

		if (leftDated !== rightDated) return leftDated ? -1 : 1;
		if (leftDated && leftTime !== rightTime) return rightTime - leftTime;

		return (left?.name ?? '').localeCompare(right?.name ?? '');
	});
